import React, { useEffect, useRef, useState } from 'react';
import { Content, ContentWrapper, Side } from './product-buy-manager-order.styles';
import { FormBuyOrder } from './form-buy-order/form-buy-order';
import { FormProductInfo } from './form-product-info/form-product-info';
import { FormRenderProps } from 'react-final-form';
import { BuyOrderFormFields } from './form-buy-order/types';
import { ProductBuyOrderProps } from './types';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useOrganizationsDetailSharedQuery } from '@entities/organization/libs/hooks/use-organization-detail-shared-query/use-organization-detail-shared-query';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { CACHE_KEY_BUY_ORDER } from '../config/api-constants';
import { ROUTE_ADMIN_APPROVE_ORDERS } from '@shared/config/routes/routes';
import toast from 'react-hot-toast';
import { Toast } from '@shared/ui/toast/toast';
import { useOrderSharedQuery } from '@entities/order/libs/hooks/use-order-shared-query/use-order-shared-query';
import { useBuyOrderMutation } from '../api/buy-order-api';
import { orderErrorByStatus } from '../lib/utils/order-error-by-status/order-error-by-status';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { useRedirect } from '@shared/lib/hooks/use-redirect/use-redirect';

/* Компонент для приоберетния платного продукта менеджером */
export const ProductBuyManagerOrder: React.FC<ProductBuyOrderProps> = ({ orderId }) => {
    const redirect = useRedirect();
    const handleSubmitRef = useRef<
        FormRenderProps<BuyOrderFormFields, Partial<BuyOrderFormFields>>['handleSubmit'] | null
    >(null);
    const user = useCurrentUserSelector();
    /*
        Отдельный статус загрузки вместо getOrderResult
        чтобы лоадер появлялся только при инициализации
        и не был виден при обновлении данных формы 
    */
    const [isLoadingPage, setIsLoadingPage] = useState(true);

    const [getOrder, getOrderResult, resetOrder] = useOrderSharedQuery(orderId);
    const [, , prefetchOrganization, resetOrganization] = useOrganizationsDetailSharedQuery(user?.company?.id || 0);
    const [, buyOrderResult] = useBuyOrderMutation({ fixedCacheKey: CACHE_KEY_BUY_ORDER });

    const order = getOrderResult.data;
    const orderError = getQueryError(getOrderResult) || orderErrorByStatus(order);

    useEffect(() => {
        (async function () {
            setIsLoadingPage(true);
            await getOrder(orderId);
            setIsLoadingPage(false);
        })();
    }, [orderId]);

    useEffect(() => {
        prefetchOrganization();
    }, []);

    useEffect(() => {
        const product = buyOrderResult?.data?.product;
        const isFree = product && Number(product.price) === 0;
        if (buyOrderResult.isSuccess) {
            toast.custom((t) => {
                return (
                    <Toast
                        type='success'
                        title='Сделка успешно создана!'
                        description={
                            isFree ? 'Продукт успешно приобретен' : 'Срок выставления счета до 3-х рабочих дней'
                        }
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            buyOrderResult.reset();
            resetOrganization();
            resetOrder();
            redirect({ url: ROUTE_ADMIN_APPROVE_ORDERS });
        } else if (buyOrderResult.isError) {
            const errorMessage = getQueryError(buyOrderResult)?.message;
            toast.custom((t) => {
                return (
                    <Toast
                        type='error'
                        title='Ошибка!'
                        description={`Возникла ошибка при отправке запроса.\n${errorMessage}`}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            buyOrderResult.reset();
        }
    }, [buyOrderResult.isSuccess, buyOrderResult.isError]);

    return (
        <ContentWrapper>
            <NetworkStateNew
                isLoading={isLoadingPage}
                isInitialized={Boolean(order)}
                isError={Boolean(orderError)}
                hasData={Boolean(order)}
                isFiltered={false}
                errorProps={{
                    title: 'Ошибка загрузки сделки',
                    description: orderError?.message,
                }}
                emptyProps={{
                    title: 'Ошибка загрузки сделки',
                }}>
                {order && (
                    <>
                        <Content>
                            <FormBuyOrder order={order} handleSubmitRef={handleSubmitRef} />
                        </Content>
                        <Side>
                            <FormProductInfo order={order} handleSubmitRef={handleSubmitRef} />
                        </Side>
                    </>
                )}
            </NetworkStateNew>
        </ContentWrapper>
    );
};
